import { Tooltip } from 'bootstrap';
import { DirectiveBinding } from 'vue';
import { OrganizationData, OrgState, UserData } from './backend';

export interface Displayable {
  readonly displayName: string;
}

export class DisplayableOrg implements OrganizationData, Displayable, Record<string, any> {
  id: string;

  name: string;

  crmNo: string;

  state?: OrgState;

  constructor(x: OrganizationData) {
    this.id = x.id;
    this.name = x.name;
    this.crmNo = x.crmNo;
    this.state = x.state;
  }

  get displayName() {
    return `${this.name} ${this.crmNo}`;
  }

  get isInactive() {
    return this.state === 'inactive';
  }
}

export const arraySize = (a?: unknown[] | null) => (a ? a.length : 0);

/**
 * check if the arrays contain same items. duplicated or the order are ignored.
 */
export const equalSets = <T>(x?: T[], y?: T[]) => {
  const xl = arraySize(x);
  if (xl !== arraySize(y)) return false;
  if (xl === 0) return true;
  if (!x || !y) return false; // not necessary by login - but required for TypeScript check

  for (let i = 0; i < xl; i++) {
    if (!y?.includes(x[i])) {
      // console.debug(`y doesn't include ${x[i]}`, y);
      return false;
    }
  }
  return true;
};

/**
 * add all items from 2 arrays to a new array, only 1 time
 * @param x
 * @param y
 */
export const unionSet = <Type>(x?: Type[], y?: Type[]) => {
  const all = (!x || !y) ? (x || y || []) : [...x, ...y];
  return all.filter((value, pos) => {return all.indexOf(value) == pos;});
};

/**
 * add all items common to 2 arrays to a new array, only 1 time
 * @param x
 * @param y
 */
export const intersectionSet = <Type>(x?: Type[], y?: Type[]) => {
  if (!x || !y || !arraySize(x) || !arraySize(y)) return [];
  return x.filter((value, _pos) => {return y.indexOf(value) >= 0;});
};

export const isEmpty = (a: unknown[]) => !a || a.length === 0;

export const tooltipDirective = (el: HTMLElement, binding: DirectiveBinding) => {
  el.setAttribute('data-bs-toggle', 'tooltip');
  el.setAttribute('data-bs-title', binding.value);
  Tooltip.getOrCreateInstance(el);
};

export function removeItem<T>(arr: Array<T>, value: T): Array<T> {
  const index = arr.indexOf(value);
  if (index > -1) {
    arr.splice(index, 1);
  }
  return arr;
}

export function contains<T>(a: Readonly<Array<T>> | undefined | null, item: T): boolean {
  return !!a && a?.indexOf(item) >= 0;
}

export function containsOneOf<T>(
  a: Readonly<Array<T>> | undefined | null,
  items: Readonly<Array<T>> | undefined,
): boolean {
  return !!a && !!items && a.some((x) => items.indexOf(x) >= 0);
}

export function containsStr(str?: string | null, substr?: string | null): boolean {
  if (str === substr) return true;
  if (str === undefined || str === null || substr === undefined || substr === null) return false;
  return str.toLowerCase()
    .includes(substr.toLowerCase());
}

export function crmLink(crmId: string, entity: 'contact' | 'account' = 'contact'): string {
  const link = window.APP_CONFIG.crm.userLink as string;
  return link.replace(/\{\{entity}}/, entity).replace(/\{\{crm-id}}/, crmId);
}

export function localeDateFormat(sdate?: string | Date | null, locales?: Intl.LocalesArgument) {
  // requested by Hermann
  if (!sdate) return '';
  const date = sdate instanceof Date ? sdate : new Date(sdate);
  return date.toLocaleString(locales === 'en' ? 'en-gb' : locales);
}

export function compareStr(a: string, b: string) {
  if (a < b) return -1;
  return a === b ? 0 : 1;
}

export function isStihlEmployee(email: string): boolean {
  return !!email && /@stihl\.\w{2,3}/.test(email);
}

export const toISODate = (date: string | undefined) => date ? new Date(date).toISOString().substring(0, 10) : undefined;

export const displayUser = (user?: { firstname: string, lastname: string }): string => (!user ? '' : `${user.firstname} ${user.lastname}`);

/**
 * retrieve filename from content-disposition header
 * @param disposition content-disposition header value
 * @param _def default value if header not set
 */
export function getFileName(disposition: string | null, _def: string): string {
  if (!disposition) return _def;

  const asciiFilenameRegex = /^filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i;

  // prevent ReDos attacks by anchoring the ascii regex to string start and
  //  slicing off everything before 'filename='
  const filenameStart = disposition?.toLowerCase().indexOf('filename=');
  if (filenameStart >= 0) {
    const partialDisposition = disposition.slice(filenameStart);
    const matches = asciiFilenameRegex.exec(partialDisposition);
    if (matches != null && matches[2]) {
      return matches[2];
    }
  }
  return _def;
}

export const isPrimaryContact = (u: UserData): boolean => arraySize(u.managedAsPrimaryContact) > 0;

